import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CategoryDto, SubCategoryDto } from '../types/categoryTypes';
import { useCategories } from '../hooks/useCategories';
import { useDeleteCategory } from '../hooks/useCategoryMutations';
import { CategoryCard } from '../components/CategoryCard';
import { SubCategoryCard } from '../components/SubCategoryCard';
import { CategoryFormModal } from '../components/CategoryFormModal';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { CartModal } from '../components/CartModal';
import { SearchModal } from '../components/SearchModal';
import '../styles/CategoryList.css';

type EditTarget = CategoryDto | SubCategoryDto | null;

interface DeleteTarget {
  id: number;
  name: string;
}

type Department = 'מוסדי' | 'קמעונאי';

export function CategoryList(): JSX.Element {
  const { user, isAdmin, logout } = useAuth();
  const { cartCount } = useCart();
  const [isCartOpen, setIsCartOpen]     = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Auto-expand parent category when returning from a subcategory product page
  useEffect(() => {
    const expandId = searchParams.get('expand');
    if (expandId) setExpandedId(Number(expandId));
  }, [searchParams]);
  const [department, setDepartment] = useState<Department | null>(() => {
    const saved = localStorage.getItem('dept') as Department | null;
    return saved === 'קמעונאי' ? 'קמעונאי' : 'מוסדי';
  });

  // Persist department choice whenever user changes it
  function handleDepartment(d: Department | null): void {
    if (d) localStorage.setItem('dept', d);
    else   localStorage.removeItem('dept');
    setDepartment(d);
  }

  const { data, isLoading, isError, error } = useCategories(department ?? undefined);
  const deleteMutation = useDeleteCategory();

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const categories = data?.categories ?? [];

  function handleToggle(id: number): void {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  // Close the open panel when focus moves to a different card
  function handleCardFocus(id: number): void {
    setExpandedId((prev) => (prev !== null && prev !== id ? null : prev));
  }

  function handleEditCategory(id: number): void {
    const found = categories.find((c) => c.id === id);
    if (found) {
      setEditTarget(found);
      setIsFormOpen(true);
    }
  }

  function handleEditSubCategory(id: number): void {
    for (const cat of categories) {
      const sub = cat.subCategories.find((s) => s.id === id);
      if (sub) {
        setEditTarget(sub);
        setIsFormOpen(true);
        return;
      }
    }
  }

  function handleDeleteRequest(id: number, name: string): void {
    setDeleteTarget({ id, name });
    setDeleteError(null);
  }

  async function handleDeleteConfirm(): Promise<void> {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed.';
      if (msg.includes('409')) {
        setDeleteError('לא ניתן למחוק קטגוריה שיש לה תת-קטגוריות. יש למחוק אותן תחילה.');
      } else {
        setDeleteError(msg);
      }
    }
  }

  function handleFormClose(): void {
    setIsFormOpen(false);
    setEditTarget(null);
  }

  if (isLoading) {
    return (
      <div className="category-list-page">
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען קטגוריות...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="category-list-page">
        <div className="error-state">
          <p>שגיאה בטעינת הקטגוריות.</p>
          <p>{error instanceof Error ? error.message : 'שגיאה לא ידועה'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="category-list-page">
      <div className="category-list-header">
        <div className="header-logo-wrap">
          <img src="/logo.png" alt="מטעמי העיר" className="header-logo" />
        </div>

        <div className="department-filter" dir="rtl" aria-label="סינון לפי מחלקה">
          <button
            className={`dept-btn${department === 'מוסדי' ? ' dept-btn--active' : ''}`}
            onClick={() => handleDepartment(department === 'מוסדי' ? null : 'מוסדי')}
          >
            מוסדי
          </button>
          <button
            className={`dept-btn${department === 'קמעונאי' ? ' dept-btn--active' : ''}`}
            onClick={() => handleDepartment(department === 'קמעונאי' ? null : 'קמעונאי')}
          >
            קמעונאי
          </button>
        </div>

        <div className="category-list-header-actions">
          <button
            className="search-open-btn"
            onClick={() => setIsSearchOpen(true)}
            aria-label="חיפוש מתקדם"
            title="חיפוש מתקדם"
          >
            🔍 חיפוש
          </button>
          {isAdmin && (
            <button
              className="add-category-btn"
              onClick={() => { setEditTarget(null); setIsFormOpen(true); }}
            >
              + הוסף קטגוריה
            </button>
          )}
          {!isAdmin && (
            <button
              className={`cart-icon-btn${cartCount > 0 ? ' cart-icon-btn--has-items' : ''}`}
              title={cartCount > 0 ? `${cartCount} מוצרים בסל` : 'הסל ריק'}
              aria-label={`סל קניות — ${cartCount} פריטים`}
              onClick={cartCount > 0 ? () => setIsCartOpen(true) : undefined}
            >
              🛒
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount}</span>
              )}
            </button>
          )}
          <div className="user-badge">
            <span className="user-badge__name">{user?.username}</span>
            <button
              className="logout-btn"
              onClick={() => { logout(); navigate('/login', { replace: true }); }}
              title="התנתק"
            >
              יציאה
            </button>
          </div>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="empty-state">
          {department
            ? `אין קטגוריות עם מוצרים ${department}.`
            : 'אין קטגוריות עדיין. הוסף קטגוריה להתחלה.'}
        </div>
      ) : (
        <div className="category-grid">
          {categories.map((cat) => (
            <div key={cat.id} className="category-grid-item">
              <CategoryCard
                category={cat}
                onEdit={handleEditCategory}
                onDelete={(id) => handleDeleteRequest(id, cat.nameEn ?? cat.nameHe)}
                onToggle={handleToggle}
                onFocusCard={handleCardFocus}
                isExpanded={expandedId === cat.id}
                department={department ?? ''}
                isAdmin={isAdmin}
              />
              {expandedId === cat.id && cat.subCategories.length > 0 && (
                <div className="subcategory-panel">
                  <p className="subcategory-panel-title">תתי קטגוריות</p>
                  <div className="subcategory-chips">
                    {cat.subCategories.map((sub) => (
                      <SubCategoryCard
                        key={sub.id}
                        subCategory={sub}
                        onEdit={handleEditSubCategory}
                        onDelete={(id) => handleDeleteRequest(id, sub.nameEn ?? sub.nameHe)}
                        department={department ?? ''}
                        isAdmin={isAdmin}
                        parentId={cat.id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CategoryFormModal
        isOpen={isFormOpen}
        onClose={handleFormClose}
        editCategory={editTarget}
        categories={categories}
      />

      <CartModal isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        categories={categories}
      />

      <DeleteConfirmModal
        isOpen={deleteTarget !== null}
        categoryName={deleteTarget?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteError}
      />
    </div>
  );
}
